import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Shield, ArrowLeft, Database } from "lucide-react";
import PublicNavbar from "../components/PublicNavbar";
import { usePageContent, renderHTML } from "../hooks/usePageContent";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PrivacyPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const { getContent } = usePageContent('privacy');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        setSettings(response.data);
        if (response.data.logo_url) setLogoUrl(response.data.logo_url);
      } catch (error) {
        console.error("Failed to fetch settings");
      }
    };
    fetchSettings();
  }, []);

  const siteName = settings?.site_name || "AIQueryStudio";
  const companyName = settings?.company_name || "AIQueryStudio Inc.";
  const companyEmail = settings?.company_email || "support@aiquerystudio.com";
  const companyWebsite = settings?.company_website || "https://aiquerystudio.com";

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <PublicNavbar />

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl mt-16">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-10 h-10 text-primary" />
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
        </div>
        
        <p className="text-muted-foreground mb-8">
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Welcome to {siteName}. We respect your privacy and are committed to protecting your personal data. 
              This privacy policy will inform you about how we look after your personal data when you visit our 
              website and use our services, and tell you about your privacy rights and how the law protects you.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect and process the following types of personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
              <li><strong>Database Connection Details:</strong> Connection strings, database configurations (encrypted)</li>
              <li><strong>Usage Data:</strong> Query history, saved queries, connection usage</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use your personal data for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>To provide and maintain our database management services</li>
              <li>To process your transactions and manage your subscription</li>
              <li>To send you service-related notifications and updates</li>
              <li>To improve and personalize your experience</li>
              <li>To detect and prevent fraud and security issues</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data against 
              unauthorized or unlawful processing, accidental loss, destruction, or damage. All database connection 
              strings and sensitive information are encrypted. We use secure HTTPS connections for all data transmission.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal data only for as long as necessary to fulfill the purposes for which we collected 
              it, including for the purposes of satisfying any legal, accounting, or reporting requirements. Account 
              data is retained as long as your account is active. Query history and saved queries are retained until 
              you delete them or close your account.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Under data protection laws, you have rights including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Right to Access:</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of processing</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our service and store certain 
              information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being 
              sent. However, if you do not accept cookies, you may not be able to use some parts of our service.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the following third-party services:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Stripe:</strong> Payment processing (see Stripe's privacy policy)</li>
              <li><strong>Google OAuth:</strong> Optional authentication service (see Google's privacy policy)</li>
              <li><strong>OpenAI:</strong> AI-powered query assistance (queries are processed but not stored by OpenAI)</li>
            </ul>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update our privacy policy from time to time. We will notify you of any changes by posting the 
              new privacy policy on this page and updating the "Last updated" date. You are advised to review this 
              privacy policy periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this privacy policy or our privacy practices, please contact us:
            </p>
            <div className="mt-4 space-y-2 text-muted-foreground">
              <p><strong>Company:</strong> {companyName}</p>
              <p><strong>Email:</strong> <a href={`mailto:${companyEmail}`} className="text-primary hover:underline">{companyEmail}</a></p>
              <p><strong>Website:</strong> <a href={companyWebsite} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{companyWebsite}</a></p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-5 h-5 object-contain" />
            ) : (
              <Database className="w-5 h-5 text-primary" />
            )}
            <span className="font-semibold">{siteName}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button onClick={() => navigate('/about')} className="text-sm text-muted-foreground hover:text-foreground">
              About
            </button>
            <button onClick={() => navigate('/privacy')} className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </button>
            <button onClick={() => navigate('/#support')} className="text-sm text-muted-foreground hover:text-foreground">
              FAQ
            </button>
            <a 
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Admin Login
            </a>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
